import React from "react";
import { AddressFields } from "./Basket";

const createAddress = (deliveryAddress) => ({
  name: deliveryAddress?.del_name || "",
  addr1: deliveryAddress?.del_add1 || "",
  addr2: deliveryAddress?.del_add2 || "",
  addr3: deliveryAddress?.del_add4 || "",
  town: deliveryAddress?.del_add3 || "",
  postcode: deliveryAddress?.del_postcode || "",
  country: deliveryAddress?.del_country || "GB",
});

const isAddressComplete = (address) =>
  ["name", "addr1", "town", "postcode", "country"].every((field) =>
    String(address[field] || "").trim()
  );

const toDeliveryAddress = (address, currentAddress) => ({
  ...currentAddress,
  del_name: address.name.trim(),
  del_add1: address.addr1.trim(),
  del_add2: address.addr2.trim(),
  del_add3: address.town.trim(),
  del_add4: address.addr3.trim(),
  del_postcode: address.postcode.trim().toUpperCase(),
  del_country: address.country.trim().toUpperCase(),
});

export default function DeliveryAddressModal({
  visible,
  reference,
  referenceType,
  deliveryAddress,
  onClose,
  onSaved,
}) {
  const [address, setAddress] = React.useState(() =>
    createAddress(deliveryAddress)
  );
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    if (!visible) return;
    setAddress(createAddress(deliveryAddress));
    setSubmitting(false);
    setError("");
  }, [visible, deliveryAddress]);

  if (!visible) return null;

  const handleChange = (field, value) => {
    setAddress((current) => ({ ...current, [field]: value }));
    setError("");
  };

  const handleAddressSelect = (selectedAddress) => {
    setAddress((current) => ({
      ...current,
      name: selectedAddress.Company || current.name,
      addr1: selectedAddress.Line1 || "",
      addr2: selectedAddress.Line2 || "",
      addr3: selectedAddress.Line3 || "",
      town: selectedAddress.City || "",
      postcode: (selectedAddress.PostalCode || "").toUpperCase(),
      country: selectedAddress.CountryIso2 || "GB",
    }));
    setError("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!isAddressComplete(address) || !reference || submitting) return;

    const nextDeliveryAddress = toDeliveryAddress(address, deliveryAddress);
    const payload = {
      ref: reference,
      type: referenceType,
      del_name: nextDeliveryAddress.del_name,
      del_add1: nextDeliveryAddress.del_add1,
      del_add2: nextDeliveryAddress.del_add2,
      del_add3: nextDeliveryAddress.del_add3,
      del_add4: nextDeliveryAddress.del_add4,
      del_postcode: nextDeliveryAddress.del_postcode,
      del_country: nextDeliveryAddress.del_country,
    };

    setSubmitting(true);
    setError("");

    try {
      const response = await fetch(
        "https://payments.fhbrundle.co.uk/api?cmd=addr_change",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );
      const responseText = await response.text();
      let responseData = null;
      try {
        responseData = responseText ? JSON.parse(responseText) : null;
      } catch {
        responseData = null;
      }

      if (!response.ok || responseData?.success === false) {
        throw new Error(
          responseData?.reason ||
            responseData?.message ||
            "The delivery address could not be updated."
        );
      }

      onSaved?.(responseData?.del_addr || nextDeliveryAddress);
      onClose?.();
    } catch (submitError) {
      setError(
        submitError?.message ||
          "There was a problem updating the delivery address. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="modal_overlay delivery-address-modal-overlay"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !submitting) onClose();
      }}
    >
      <section
        className="modal_content delivery-address-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="delivery-address-modal-title"
      >
        <div className="delivery-address-modal__heading">
          <div>
            <span className="basket-panel-eyebrow">Delivery details</span>
            <h3 id="delivery-address-modal-title">Alter delivery address</h3>
            <p>
              Search for an address or enter the delivery details manually.
            </p>
          </div>
          <button
            type="button"
            className="basket-debug-modal__close"
            aria-label="Close delivery address"
            onClick={onClose}
            disabled={submitting}
          >
            &times;
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <AddressFields
            idPrefix="delivery-change"
            required
            value={address}
            onChange={handleChange}
            onAddressSelect={handleAddressSelect}
          />

          {error && (
            <p className="delivery-address-modal__message is-error" role="alert">
              {error}
            </p>
          )}
          <div className="delivery-address-modal__actions">
            <button
              type="button"
              className="button-secondary"
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="pay_button"
              disabled={!isAddressComplete(address) || submitting}
            >
              {submitting ? "Updating…" : "Update delivery address"}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}
